import { z } from "zod";

export const INVALID_EMAIL_MESSAGE = "Invalid email address";

const INSTITUTIONAL_LABELS = new Set(["edu", "gov"]);
const INSTITUTIONAL_LABEL_SUFFIXES = ["sd", "ps", "pcs"];
const INSTITUTIONAL_LABEL_TOKENS = [
  "k12",
  "school",
  "district",
  "academy",
  "charter",
  "prep",
  "boces",
];
const PERSONAL_DOMAINS = new Set(["charter.net"]);

export function isInstitutionalEmailDomain(emailAddress: Email): boolean {
  const domain = emailAddress.toLowerCase().split("@").pop() ?? "";
  if (PERSONAL_DOMAINS.has(domain)) return false;

  const labels = domain.split(".");
  if (labels.some((label) => INSTITUTIONAL_LABELS.has(label))) return true;

  return labels
    .slice(0, -1)
    .some(
      (label) =>
        INSTITUTIONAL_LABEL_SUFFIXES.some((suffix) => label.endsWith(suffix)) ||
        INSTITUTIONAL_LABEL_TOKENS.some((token) => label.includes(token)),
    );
}

export const emailSchema = z
  .string()
  .transform((val) => val.replace(/ /g, "+"))
  .pipe(z.email(INVALID_EMAIL_MESSAGE));

export function parseEmail(rawEmail: string | null): string | undefined {
  if (!rawEmail) return undefined;
  const result = emailSchema.safeParse(rawEmail);
  return result.success ? result.data : undefined;
}

const brandedEmailSchema = emailSchema.brand<"Email">();

export type Email = z.infer<typeof brandedEmailSchema>;

export const email = (value: string): Email => brandedEmailSchema.parse(value);

export function isInstitutionalEmailInput(value: string): boolean {
  const result = brandedEmailSchema.safeParse(value.trim());
  return result.success && isInstitutionalEmailDomain(result.data);
}
